import { useBranding } from "@/context/BrandingContext";

/**
 * BrandLogo — renders the Conduit v2 banner logo, auto-swapping between
 * light and dark variants based on the current theme (`.dark` on <html>).
 *
 * Precedence per theme:
 *   1. Tenant per-theme upload (`brand.logo_url_light` / `logo_url_dark`)
 *   2. Tenant single-logo upload (`brand.logo_url`)          — legacy
 *   3. Shipped Conduit v2 default (`/brand/conduit-v2-*.png`)
 */
export function BrandLogo({ className = "h-9", showFallbackText = false }) {
  const { brand } = useBranding();

  // Iter 111 — Per-theme URLs win. Falls back to the single legacy
  // logo_url when a theme-specific one isn't set so old tenants keep
  // working without any admin action.
  const lightSrc = brand.logo_url_light || brand.logo_url || "/brand/conduit-v2-light.png?v=3";
  const darkSrc  = brand.logo_url_dark  || brand.logo_url || "/brand/conduit-v2-dark.png?v=3";
  const hasCustomUpload = !!(brand.logo_url_light || brand.logo_url_dark || brand.logo_url);

  // When a tenant has only the legacy single logo_url set, keep the
  // "boxed white background" treatment they used to get (dark PNGs
  // needed it). Once they upload theme-specific variants we drop the
  // box because those variants are designed to sit on either surface.
  const legacyBox = brand.logo_url && !brand.logo_url_light && !brand.logo_url_dark;
  const imgCls = `${className} w-auto object-contain${legacyBox ? " rounded-lg bg-white" : ""}`;

  return (
    <div className="flex items-center gap-2.5">
      <img
        src={lightSrc}
        alt={brand.brand_name || "Conduit v2"}
        className={`${imgCls} block dark:hidden`}
        data-testid="brand-logo-light"
      />
      <img
        src={darkSrc}
        alt={brand.brand_name || "Conduit v2"}
        className={`${imgCls} hidden dark:block`}
        data-testid="brand-logo-dark"
      />
      {(hasCustomUpload && showFallbackText) && (
        <div className="leading-tight">
          <div className="font-heading text-[15px] font-bold text-slate-900 dark:text-slate-100">{brand.brand_name}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{brand.brand_subtitle || "Admin"}</div>
        </div>
      )}
    </div>
  );
}
